import { Slicer, createFormatter, addDays, getDateMeta, buildDateStr, formatDayString, buildNavLinkAttrs, joinClassNames, getDayClassName, getEventKey, BaseComponent, StandardEvent, buildEventRangeTimeText, getEventTagAndAttrs, EventContainer, MoreLinkContainer, getEventRangeMeta, DateComponent, memoize, setRef, watchSize, isDimsEqual, hasCustomDayCellContent, DayCellContainer, addMs, SegHierarchy, DaySeriesModel, DayTableModel, fracToCssDim, watchHeight, RefMap, afterSize, sortEventSegs, WeekNumberContainer, buildEventRangeKey, BgEvent, renderFill, getIsHeightAuto, ContentContainer, renderText, getStickyHeaderDates, Scroller, Ruler, getStickyFooterScrollbar, FooterScrollbar, getScrollerSyncerClass, ViewContainer, NowTimer, DateProfileGenerator, addWeeks, diffWeeks, injectStyles } from '@fullcalendar/core/internal.js';
import { createElement, Fragment, createRef, Component } from '@fullcalendar/core/preact.js';

class DayTableSlicer extends Slicer {
    constructor() {
        super(...arguments);
        this.forceDayIfListItem = true;
    }
    sliceRange(dateRange, dayTableModel) {
        return dayTableModel.sliceRange(dateRange);
    }
}

// TODO: converge types with DayTableCell and DayCellContainer (the component) and refineRenderProps
// the generation of DayTableCell will be distinct (for the BODY cells)
// but can share some of the same types/utils
// Date Cells
// -------------------------------------------------------------------------------------------------
const WEEKDAY_FORMAT = createFormatter({ weekday: 'long' });
const firstSunday = new Date(259200000);
function buildDateRowConfigs(...args) {
    return [buildDateRowConfig(...args)];
}
/*
Should this receive resource data attributes?
Or ResourceApi object itself?
*/
function buildDateRowConfig(dates, datesRepDistinctDays, dateProfile, todayRange, dayHeaderFormat, // TODO: rename to dateHeaderFormat?
context, colSpan) {
    return {
        isDateRow: true,
        renderConfig: buildDateRenderConfig(context),
        dataConfigs: buildDateDataConfigs(dates, datesRepDistinctDays, dateProfile, todayRange, dayHeaderFormat, context, colSpan)
    };
}
/*
For header cells: how to connect w/ custom rendering
Applies to all cells in a row
*/
function buildDateRenderConfig(context) {
    const { options } = context;
    return {
        generatorName: 'dayHeaderContent',
        customGenerator: options.dayHeaderContent,
        classNameGenerator: options.dayHeaderClassNames,
        didMount: options.dayHeaderDidMount,
        willUnmount: options.dayHeaderWillUnmount,
    };
}
const dowDates = [];
for (let dow = 0; dow < 7; dow++) {
    dowDates.push(addDays(new Date(259200000), dow)); // start with Sun, 04 Jan 1970 00:00:00 GMT)
}
/*
For header cells: data
*/
function buildDateDataConfigs(dates, datesRepDistinctDays, dateProfile, todayRange, dayHeaderFormat, // TODO: rename to dateHeaderFormat?
context, colSpan = 1, keyPrefix = '', extraRenderProps = {}, // TODO
extraAttrs = {}, // TODO
className = '') {
    const { dateEnv, viewApi, options } = context;
    return datesRepDistinctDays
        ? dates.map((date) => {
            const dateMeta = getDateMeta(date, todayRange, null, dateProfile);
            const text = dateEnv.format(date, dayHeaderFormat);
            const renderProps = Object.assign(Object.assign(Object.assign({}, dateMeta), { date: dateEnv.toDate(date), view: viewApi, text }), extraRenderProps);
            const isNavLink = options.navLinks && !dateMeta.isDisabled &&
                dates.length > 1; // don't show navlink to day if only one day
            const fullDateStr = buildDateStr(context, date);
            // for DayGridHeaderCell
            return {
                key: keyPrefix + date.toUTCString(),
                renderProps,
                attrs: Object.assign(Object.assign(Object.assign({ 'aria-label': fullDateStr }, (dateMeta.isToday ? { 'aria-current': 'date' } : {})), { 'data-date': formatDayString(date) }), extraAttrs),
                // for navlink
                innerAttrs: isNavLink
                    ? buildNavLinkAttrs(context, date, undefined, fullDateStr)
                    : { 'aria-hidden': true },
                colSpan,
                isNavLink,
                className: joinClassNames(className, getDayClassName(dateMeta)),
            };
        })
        : dates.map((date) => {
            const dow = date.getUTCDay();
            const normDate = addDays(firstSunday, dow);
            const dayMeta = {
                dow,
                isDisabled: false,
                isFuture: false,
                isPast: false,
                isToday: false,
                isOther: false,
            };
            const text = dateEnv.format(normDate, dayHeaderFormat);
            const renderProps = Object.assign(Object.assign(Object.assign({}, dayMeta), { date: dowDates[dow], view: viewApi, text }), extraRenderProps);
            const fullWeekDayStr = dateEnv.format(normDate, WEEKDAY_FORMAT);
            // for DayGridHeaderCell
            return {
                key: keyPrefix + String(dow),
                renderProps,
                attrs: Object.assign({ 'aria-label': fullWeekDayStr }, extraAttrs),
                // NOT a navlink
                innerAttrs: {
                    'aria-hidden': true, // label already on cell
                },
                colSpan,
                className: joinClassNames(className, getDayClassName(dayMeta)),
            };
        });
}

/*
We need really specific keys because RefMap::createRef() which is then given to heightRef
unable to change key! As a result, we cannot reuse elements between normal/slice/standin types,
but that's okay since they render quite differently
*/
function getEventPartKey(seg) {
    return getEventKey(seg) + ':' + seg.start +
        (seg.standinFor ? ':standin' : seg.isSlice ? ':slice' : '');
}
// DayGridRange utils (TODO: move)
// -------------------------------------------------------------------------------------------------
function splitSegsByRow(segs, rowCnt) {
    const byRow = [];
    for (let row = 0; row < rowCnt; row++) {
        byRow[row] = [];
    }
    for (const seg of segs) {
        byRow[seg.row].push(seg);
    }
    return byRow;
}
function splitInteractionByRow(ui, rowCnt) {
    const byRow = [];
    if (!ui) {
        for (let row = 0; row < rowCnt; row++) {
            byRow[row] = null;
        }
    }
    else {
        for (let row = 0; row < rowCnt; row++) {
            byRow[row] = {
                affectedInstances: ui.affectedInstances,
                isEvent: ui.isEvent,
                segs: [],
            };
        }
        for (const seg of ui.segs) {
            byRow[seg.row].segs.push(seg);
        }
    }
    return byRow;
}
function sliceSegForCol(seg, col) {
    return Object.assign(Object.assign({}, seg), { start: col, end: col + 1, isStart: seg.isStart && seg.start === col, isEnd: seg.isEnd && seg.end - 1 === col, standinFor: seg });
}

const DEFAULT_TABLE_EVENT_TIME_FORMAT = createFormatter({
    hour: 'numeric',
    minute: '2-digit',
    omitZeroMinute: true,
    meridiem: 'narrow',
});
function hasListItemDisplay(seg) {
    let { display } = seg.eventRange.ui;
    return display === 'list-item' || (display === 'auto' &&
        !seg.eventRange.def.allDay &&
        (seg.end - seg.start) === 1 && // single-day
        seg.isStart && // "
        seg.isEnd // "
    );
}

class DayGridBlockEvent extends BaseComponent {
    render() {
        let { props } = this;
        return (createElement(StandardEvent, Object.assign({}, props, { className: 'fc-daygrid-block-event fc-daygrid-event fc-h-event', defaultTimeFormat: DEFAULT_TABLE_EVENT_TIME_FORMAT, defaultDisplayEventEnd: props.defaultDisplayEventEnd, disableResizing: !props.eventRange.def.allDay })));
    }
}

class DayGridListEvent extends BaseComponent {
    render() {
        let { props, context } = this;
        let { options } = context;
        let { eventRange } = props;
        let timeFormat = options.eventTimeFormat || DEFAULT_TABLE_EVENT_TIME_FORMAT;
        let timeText = buildEventRangeTimeText(timeFormat, eventRange, 
        /* slicedStart = */ undefined, 
        /* slicedEnd = */ undefined, props.isStart, props.isEnd, context, 
        /* defaultDisplayEventTime = */ true, props.defaultDisplayEventEnd);
        let [tag, attrs] = getEventTagAndAttrs(eventRange, context);
        return (createElement(EventContainer, Object.assign({}, props, { tag: tag, attrs: attrs, className: 'fc-daygrid-dot-event fc-daygrid-event', defaultGenerator: renderInnerContent, timeText: timeText, isResizing: false, isDateSelecting: false })));
    }
}
function renderInnerContent(renderProps) {
    return (createElement(Fragment, null,
        createElement("div", { className: "fc-daygrid-event-dot", style: { borderColor: renderProps.borderColor || renderProps.backgroundColor } }),
        renderProps.timeText && (createElement("div", { className: "fc-event-time" }, renderProps.timeText)),
        createElement("div", { className: "fc-event-title" }, renderProps.event.title || createElement(Fragment, null, "\u00A0"))));
}

class DayGridMoreLink extends BaseComponent {
    render() {
        let { props } = this;
        return (createElement(MoreLinkContainer, { className: joinClassNames('fc-daygrid-more-link', props.isBlock
                ? 'fc-daygrid-more-link-block'
                : 'fc-daygrid-more-link-button'), dateProfile: props.dateProfile, todayRange: props.todayRange, allDayDate: props.allDayDate, segs: props.segs, hiddenSegs: props.hiddenSegs, alignElRef: props.alignElRef, alignParentTop: props.alignParentTop, dateSpanProps: props.dateSpanProps, popoverContent: () => {
                let forcedInvisibleMap = // TODO: more convenient/DRY
                 (props.eventDrag ? props.eventDrag.affectedInstances : null) ||
                    (props.eventResize ? props.eventResize.affectedInstances : null) ||
                    {};
                return (createElement(Fragment, null, props.segs.map((seg) => {
                    let { eventRange } = seg;
                    let { instanceId } = eventRange.instance;
                    return (createElement("div", { key: instanceId, style: {
                            visibility: forcedInvisibleMap[instanceId] ? 'hidden' : '',
                        } }, hasListItemDisplay(seg) ? (createElement(DayGridListEvent, Object.assign({ eventRange: eventRange, isStart: seg.isStart, isEnd: seg.isEnd, isDragging: false, isSelected: instanceId === props.eventSelection, defaultDisplayEventEnd: false }, getEventRangeMeta(eventRange, props.todayRange)))) : (createElement(DayGridBlockEvent, Object.assign({ eventRange: eventRange, isStart: seg.isStart, isEnd: seg.isEnd, isDragging: false, isResizing: false, isDateSelecting: false, isSelected: instanceId === props.eventSelection, defaultDisplayEventEnd: false }, getEventRangeMeta(eventRange, props.todayRange))))));
                })));
            } }));
    }
}

class DayGridCell extends DateComponent {
    constructor() {
        super(...arguments);
        // memo
        this.getDateMeta = memoize(getDateMeta);
        // ref
        this.rootElRef = createRef();
        this.handleBodyEl = (bodyEl) => {
            if (this.disconnectBodyHeight) {
                this.disconnectBodyHeight();
                this.disconnectBodyHeight = undefined;
                setRef(this.props.headerHeightRef, null);
                setRef(this.props.mainHeightRef, null);
            }
            if (bodyEl) {
                // we want to fire on ANY size change, because we do more advanced stuff
                this.disconnectBodyHeight = watchSize(bodyEl, (_bodyWidth, bodyHeight) => {
                    const { props } = this;
                    const mainRect = bodyEl.getBoundingClientRect();
                    const rootRect = this.rootElRef.current.getBoundingClientRect();
                    const headerHeight = mainRect.top - rootRect.top;
                    if (!isDimsEqual(this.headerHeight, headerHeight)) {
                        this.headerHeight = headerHeight;
                        setRef(props.headerHeightRef, headerHeight);
                    }
                    if (props.fgLiquidHeight) {
                        setRef(props.mainHeightRef, bodyHeight);
                    }
                });
            }
        };
    }
    render() {
        let { props, context } = this;
        let { options, dateEnv } = context;
        // TODO: memoize this
        const isMonthStart = props.showDayNumber &&
            shouldDisplayMonthStart(props.date, props.dateProfile.currentRange, dateEnv);
        const dateMeta = this.getDateMeta(props.date, props.todayRange, null, props.dateProfile);
        const baseClassName = joinClassNames('fc-daygrid-day', props.borderStart && 'fc-border-s', props.width != null ? '' : 'fc-liquid', 'fc-flex-col');
        if (dateMeta.isDisabled) {
            return (createElement("div", { role: 'gridcell', "aria-disabled": true, className: joinClassNames(baseClassName, 'fc-day-disabled'), style: {
                    width: props.width
                } }));
        }
        const hasDayNumber = props.showDayNumber || hasCustomDayCellContent(options);
        const isNavLink = options.navLinks;
        const fullDateStr = buildDateStr(context, props.date);
        return (createElement(DayCellContainer, { tag: "div", className: joinClassNames(baseClassName, props.className), attrs: Object.assign(Object.assign({}, props.attrs), { role: 'gridcell', 'aria-label': fullDateStr }), style: {
                width: props.width
            }, elRef: this.rootElRef, renderProps: props.renderProps, defaultGenerator: renderTopInner, date: props.date, dateMeta: dateMeta, showDayNumber: props.showDayNumber, isMonthStart: isMonthStart }, (InnerContent) => (createElement(Fragment, null,
            hasDayNumber && (createElement("div", { className: "fc-daygrid-day-header" },
                createElement(InnerContent, { tag: 'div', attrs: isNavLink
                        ? buildNavLinkAttrs(context, props.date, undefined, fullDateStr)
                        : { 'aria-hidden': true } // label already on cell
                    , className: joinClassNames('fc-daygrid-day-number', isMonthStart && 'fc-daygrid-month-start') }))),
            createElement("div", { className: joinClassNames('fc-daygrid-day-body', props.isTall && 'fc-daygrid-day-body-tall', props.fgLiquidHeight ? 'fc-liquid' : 'fc-grow'), ref: this.handleBodyEl },
                createElement("div", { className: 'fc-daygrid-day-events', style: { height: props.fgHeight } }, props.fg),
                createElement(DayGridMoreLink, { isBlock: props.isCompact, allDayDate: props.date, segs: props.segs, hiddenSegs: props.hiddenSegs, alignElRef: this.rootElRef, alignParentTop: props.showDayNumber ? '[role=row]' : '.fc-view', dateSpanProps: props.dateSpanProps, dateProfile: props.dateProfile, eventSelection: props.eventSelection, eventDrag: props.eventDrag, eventResize: props.eventResize, todayRange: props.todayRange }))))));
    }
}
// Utils
// -------------------------------------------------------------------------------------------------
function renderTopInner(props) {
    return props.dayNumberText || createElement(Fragment, null, "\u00A0");
}
function shouldDisplayMonthStart(date, currentRange, dateEnv) {
    const { start: currentStart, end: currentEnd } = currentRange;
    const currentEndIncl = addMs(currentEnd, -1);
    const currentFirstYear = dateEnv.getYear(currentStart);
    const currentFirstMonth = dateEnv.getMonth(currentStart);
    const currentLastYear = dateEnv.getYear(currentEndIncl);
    const currentLastMonth = dateEnv.getMonth(currentEndIncl);
    // spans more than one month?
    return !(currentFirstYear === currentLastYear && currentFirstMonth === currentLastMonth) &&
        Boolean(
        // first date in current view?
        date.valueOf() === currentStart.valueOf() ||
            // a month-start that's within the current range?
            (dateEnv.getDay(date) === 1 && date.valueOf() < currentEnd.valueOf()));
}

function computeFgSegVerticals(segs, segHeightMap, cells, maxHeight, strictOrder, allowSlicing = true, dayMaxEvents, dayMaxEventRows) {
    let maxCoord;
    let maxDepth;
    let hiddenConsumes;
    if (dayMaxEvents === true || dayMaxEventRows === true) {
        maxCoord = maxHeight;
        hiddenConsumes = true;
    }
    else if (typeof dayMaxEvents === 'number') {
        maxDepth = dayMaxEvents;
        hiddenConsumes = false;
    }
    else if (typeof dayMaxEventRows === 'number') {
        maxDepth = dayMaxEventRows;
        hiddenConsumes = true;
    }
    // NOTE: visibleSegsMap and hiddenSegMap map NEVER overlap for a given event
    // once a seg has a height, the combined potentially-sliced segs will comprise the entire span of the seg
    // if a seg does not have a height yet, it won't be inserted into either visibleSegsMap/hiddenSegMap
    const visibleSegMap = new Map();
    const hiddenSegMap = new Map();
    const segTops = new Map();
    const isSlicedMap = new Map();
    let hierarchy = new SegHierarchy(segs, (seg) => segHeightMap.get(getEventPartKey(seg)), strictOrder, maxCoord, maxDepth, hiddenConsumes, allowSlicing);
    hierarchy.traverseSegs((seg, segTop) => {
        addToSegMap(visibleSegMap, seg);
        segTops.set(getEventPartKey(seg), segTop);
        if (seg.isSlice) {
            isSlicedMap.set(seg.eventRange, true);
        }
    });
    for (const hiddenSeg of hierarchy.hiddenSegs) {
        addToSegMap(hiddenSegMap, hiddenSeg); // hidden main segs
    }
    // recompute tops while considering slices
    // portions of these slices might be added to hiddenSegMap
    if (isSlicedMap.size) {
        segTops.clear();
        hierarchy = new SegHierarchy(compileSegMap(segs, visibleSegMap), (seg) => segHeightMap.get(getEventPartKey(seg)), strictOrder, maxCoord, maxDepth, hiddenConsumes);
        hierarchy.traverseSegs((seg, segTop) => {
            segTops.set(getEventPartKey(seg), segTop); // newly-hidden main segs and slices
        });
        for (const hiddenSeg of hierarchy.hiddenSegs) {
            addToSegMap(hiddenSegMap, hiddenSeg);
        }
    }
    const segsByCol = [];
    const hiddenSegsByCol = [];
    const renderableSegsByCol = [];
    const heightsByCol = [];
    for (let col = 0; col < cells.length; col++) {
        segsByCol.push([]);
        hiddenSegsByCol.push([]);
        renderableSegsByCol.push([]);
        heightsByCol.push(0);
    }
    for (const seg of segs) {
        const { eventRange } = seg;
        const visibleSegs = visibleSegMap.get(eventRange) || [];
        const hiddenSegs = hiddenSegMap.get(eventRange) || [];
        const isSliced = isSlicedMap.get(eventRange) || false;
        // add orig to renderable
        renderableSegsByCol[seg.start].push(seg);
        // add slices to renderable
        if (isSliced) {
            for (const visibleSeg of visibleSegs) {
                renderableSegsByCol[visibleSeg.start].push(visibleSeg);
            }
        }
        // accumulate segsByCol/heightsByCol for visible segs
        for (const visibleSeg of visibleSegs) {
            for (let col = visibleSeg.start; col < visibleSeg.end; col++) {
                const slice = sliceSegForCol(visibleSeg, col);
                segsByCol[col].push(slice);
            }
            const segKey = getEventPartKey(visibleSeg);
            const segTop = segTops.get(segKey);
            if (segTop != null) { // positioned?
                const segHeight = segHeightMap.get(segKey);
                for (let col = visibleSeg.start; col < visibleSeg.end; col++) {
                    heightsByCol[col] = Math.max(heightsByCol[col], segTop + segHeight);
                }
            }
        }
        // accumulate segsByCol/hiddenSegsByCol for hidden segs
        for (const hiddenSeg of hiddenSegs) {
            for (let col = hiddenSeg.start; col < hiddenSeg.end; col++) {
                const slice = sliceSegForCol(hiddenSeg, col);
                segsByCol[col].push(slice);
                hiddenSegsByCol[col].push(slice);
            }
        }
    }
    return [
        segsByCol,
        hiddenSegsByCol,
        renderableSegsByCol,
        segTops,
        heightsByCol,
    ];
}
// Utils
// -------------------------------------------------------------------------------------------------
function addToSegMap(map, seg) {
    let list = map.get(seg.eventRange);
    if (!list) {
        map.set(seg.eventRange, list = []);
    }
    list.push(seg);
}
/*
Ensures relative order of DayRowEventRange stays consistent with segs
*/
function compileSegMap(segs, segMap) {
    const res = [];
    for (const seg of segs) {
        res.push(...(segMap.get(seg.eventRange) || []));
    }
    return res;
}

/*
TODO: move this so @fullcalendar/daygrid
*/
function buildDayTableModel(dateProfile, dateProfileGenerator) {
    let daySeries = new DaySeriesModel(dateProfile.renderRange, dateProfileGenerator);
    return new DayTableModel(daySeries, /year|month|week/.test(dateProfile.currentRangeUnit));
}
function computeColWidth(colCnt, colMinWidth, viewportWidth) {
    if (viewportWidth == null) {
        return [undefined, undefined];
    }
    const colTempWidth = viewportWidth / colCnt;
    if (colTempWidth < colMinWidth) {
        return [colMinWidth * colCnt, colMinWidth];
    }
    return [viewportWidth, undefined];
}
// Positioning
// -------------------------------------------------------------------------------------------------
function computeTopFromDate(date, cellRows, rowHeightMap, adjust = 0) {
    let top = 0;
    for (const cells of cellRows) {
        const start = cells[0].date;
        const end = cells[cells.length - 1].date;
        const key = start.toISOString();
        if (date >= start && date <= end) {
            return top;
        }
        const rowHeight = rowHeightMap.get(key);
        if (rowHeight == null) {
            return; // denote unknown
        }
        top += rowHeight + adjust;
    }
    return top;
}
/*
FYI, `width` is not dependable for aligning completely to farside
*/
function computeHorizontalsFromSeg(seg, colWidth, colCnt, isRtl) {
    let fromStart;
    let fromEnd;
    if (colWidth != null) {
        fromStart = seg.start * colWidth;
        fromEnd = (colCnt - seg.end) * colWidth;
    }
    else {
        const colWidthFrac = 1 / colCnt;
        fromStart = fracToCssDim(seg.start * colWidthFrac);
        fromEnd = fracToCssDim(1 - seg.end * colWidthFrac);
    }
    if (isRtl) {
        return { right: fromStart, left: fromEnd };
    }
    else {
        return { left: fromStart, right: fromEnd };
    }
}
function computeColFromPosition(positionLeft, elWidth, colWidth, colCnt, isRtl) {
    const realColWidth = colWidth != null ? colWidth : elWidth / colCnt;
    const colFromLeft = Math.floor(positionLeft / realColWidth);
    const col = isRtl ? (colCnt - colFromLeft - 1) : colFromLeft;
    const left = colFromLeft * realColWidth;
    const right = left + realColWidth;
    return { col, left, right };
}
function computeRowFromPosition(positionTop, cellRows, rowHeightMap) {
    let row = 0;
    let top = 0;
    let bottom = 0;
    for (const cells of cellRows) {
        const key = cells[0].key;
        top = bottom;
        bottom = top + rowHeightMap.get(key);
        if (positionTop < bottom) {
            break;
        }
        row++;
    }
    return { row, top, bottom };
}
// Hit Element
// -------------------------------------------------------------------------------------------------
function getRowEl(rootEl, row) {
    return rootEl.querySelectorAll('[role=row]')[row];
}
function getCellEl(rowEl, col) {
    return rowEl.querySelectorAll('[role=gridcell]')[col];
}
// Header Formatting
// -------------------------------------------------------------------------------------------------
function createDayHeaderFormatter(explicitFormat, datesRepDistinctDays, dateCnt) {
    return explicitFormat || computeFallbackHeaderFormat(datesRepDistinctDays, dateCnt);
}
// Computes a default column header formatting string if `colFormat` is not explicitly defined
function computeFallbackHeaderFormat(datesRepDistinctDays, dayCnt) {
    // if more than one week row, or if there are a lot of columns with not much space,
    // put just the day numbers will be in each cell
    if (!datesRepDistinctDays || dayCnt > 10) {
        return createFormatter({ weekday: 'short' }); // "Sat"
    }
    if (dayCnt > 1) {
        return createFormatter({ weekday: 'short', month: 'numeric', day: 'numeric', omitCommas: true }); // "Sat 11/12"
    }
    return createFormatter({ weekday: 'long' }); // "Saturday"
}

class DayGridEventHarness extends Component {
    constructor() {
        super(...arguments);
        // ref
        this.rootElRef = createRef();
    }
    render() {
        const { props } = this;
        return (createElement("div", { className: joinClassNames(props.className, 'fc-abs'), style: props.style, ref: this.rootElRef }, props.children));
    }
    componentDidMount() {
        const rootEl = this.rootElRef.current; // TODO: make dynamic with useEffect
        this.disconnectHeight = watchHeight(rootEl, (height) => {
            setRef(this.props.heightRef, height);
        });
    }
    componentWillUnmount() {
        this.disconnectHeight();
        setRef(this.props.heightRef, null);
    }
}

const DEFAULT_WEEK_NUM_FORMAT = createFormatter({ week: 'narrow' });
class DayGridRow extends BaseComponent {
    constructor() {
        super(...arguments);
        this.headerHeightRefMap = new RefMap(() => {
            afterSize(this.handleSegPositioning);
        });
        this.mainHeightRefMap = new RefMap(() => {
            const fgLiquidHeight = this.props.dayMaxEvents === true || this.props.dayMaxEventRows === true;
            if (fgLiquidHeight) {
                afterSize(this.handleSegPositioning);
            }
        });
        this.segHeightRefMap = new RefMap(() => {
            afterSize(this.handleSegPositioning);
        });
        this.handleRootEl = (rootEl) => {
            this.rootEl = rootEl;
            setRef(this.props.rootElRef, rootEl);
        };
        this.handleSegPositioning = () => {
            this.forceUpdate();
        };
    }
    render() {
        const { props, context, headerHeightRefMap, mainHeightRefMap } = this;
        const { cells } = props;
        const { options } = context;
        const weekDate = props.cells[0].date;
        const fgLiquidHeight = props.dayMaxEvents === true || props.dayMaxEventRows === true;
        // TODO: memoize? sort all types of segs?
        const fgEventSegs = sortEventSegs(props.fgEventSegs, options.eventOrder);
        // TODO: memoize?
        const [maxMainTop, minMainHeight] = this.computeFgDims(); // uses headerHeightRefMap/mainHeightRefMap
        const [segsByCol, hiddenSegsByCol, renderableSegsByCol, segTops, simpleHeightsByCol] = computeFgSegVerticals(fgEventSegs, this.segHeightRefMap.current, cells, fgLiquidHeight ? minMainHeight : undefined, // if not defined in first run, will unlimited!?
        options.eventOrderStrict, options.eventSlicing, props.dayMaxEvents, props.dayMaxEventRows);
        const heightsByCol = [];
        if (maxMainTop != null) {
            let col = 0;
            for (const cell of cells) { // uses headerHeightRefMap/maxMainTop/simpleHeightsByCol
                const cellHeaderHeight = headerHeightRefMap.current.get(cell.key);
                const extraFgHeight = maxMainTop - cellHeaderHeight;
                heightsByCol.push(simpleHeightsByCol[col++] + extraFgHeight);
            }
        }
        const highlightSegs = this.getHighlightSegs();
        const mirrorSegs = this.getMirrorSegs();
        const forcedInvisibleMap = // TODO: more convenient/DRY
         (props.eventDrag && props.eventDrag.affectedInstances) ||
            (props.eventResize && props.eventResize.affectedInstances) ||
            {};
        const isNavLink = options.navLinks;
        const fullWeekStr = buildDateStr(context, weekDate, 'week');
        return (createElement("div", { role: props.role /* !!! */, "aria-label": props.role === 'row' // HACK
                ? fullWeekStr
                : undefined // can't have label on non-role div
            , className: joinClassNames('fc-daygrid-row', props.forPrint && 'fc-daygrid-row-print', 'fc-flex-row fc-rel', props.className), style: {
                'flex-basis': props.basis,
            }, ref: this.handleRootEl },
            props.showWeekNumbers && (createElement(WeekNumberContainer, { tag: 'div', attrs: Object.assign(Object.assign({}, (isNavLink
                    ? buildNavLinkAttrs(context, weekDate, 'week', fullWeekStr, /* isTabbable = */ false)
                    : {})), { 'role': undefined, 'aria-hidden': true }), className: 'fc-daygrid-week-number', date: weekDate, defaultFormat: DEFAULT_WEEK_NUM_FORMAT })),
            this.renderFillSegs(props.businessHourSegs, 'non-business'),
            this.renderFillSegs(props.bgEventSegs, 'bg-event'),
            this.renderFillSegs(highlightSegs, 'highlight'),
            props.cells.map((cell, col) => {
                const normalFgNodes = this.renderFgSegs(maxMainTop, renderableSegsByCol[col], segTops, props.todayRange, forcedInvisibleMap);
                return (createElement(DayGridCell, { key: cell.key, dateProfile: props.dateProfile, todayRange: props.todayRange, date: cell.date, showDayNumber: props.showDayNumbers, isCompact: props.isCompact, isTall: props.isTall, borderStart: Boolean(col), 
                    // content
                    segs: segsByCol[col], hiddenSegs: hiddenSegsByCol[col], fgLiquidHeight: fgLiquidHeight, fg: (createElement(Fragment, null, normalFgNodes)), eventDrag: props.eventDrag, eventResize: props.eventResize, eventSelection: props.eventSelection, 
                    // render hooks
                    renderProps: cell.renderProps, dateSpanProps: cell.dateSpanProps, attrs: cell.attrs, className: cell.className, 
                    // dimensions
                    fgHeight: heightsByCol[col], width: props.colWidth, 
                    // refs
                    headerHeightRef: headerHeightRefMap.createRef(cell.key), mainHeightRef: mainHeightRefMap.createRef(cell.key) }));
            }),
            this.renderFgSegs(maxMainTop, mirrorSegs, segTops, props.todayRange, {}, // forcedInvisibleMap
            Boolean(props.eventDrag), Boolean(props.eventResize), false)));
    }
    renderFgSegs(headerHeight, segs, segTops, todayRange, forcedInvisibleMap, isDragging, isResizing, isDateSelecting) {
        var _a;
        const { props, context, segHeightRefMap } = this;
        const { isRtl } = context;
        const { colWidth, eventSelection } = props;
        const colCnt = props.cells.length;
        const defaultDisplayEventEnd = props.cells.length === 1;
        const isMirror = isDragging || isResizing || isDateSelecting;
        const nodes = [];
        for (const seg of segs) {
            const key = getEventPartKey(seg);
            const { standinFor, eventRange } = seg;
            const { instanceId } = eventRange.instance;
            if (standinFor) {
                continue;
            }
            const { left, right } = computeHorizontalsFromSeg(seg, colWidth, colCnt, isRtl);
            const localTop = (_a = segTops.get(standinFor ? getEventPartKey(standinFor) : key)) !== null && _a !== void 0 ? _a : (isMirror ? 0 : undefined);
            const top = headerHeight != null && localTop != null
                ? headerHeight + localTop
                : undefined;
            const isInvisible = standinFor || forcedInvisibleMap[instanceId] || top == null;
            nodes.push(createElement(DayGridEventHarness, { key: key, className: seg.start ? 'fc-border-transparent fc-border-s' : '', style: {
                    visibility: isInvisible ? 'hidden' : '',
                    top,
                    left,
                    right,
                }, heightRef: (!standinFor && !isMirror)
                    ? segHeightRefMap.createRef(key)
                    : null }, hasListItemDisplay(seg) ? (createElement(DayGridListEvent, Object.assign({ eventRange: eventRange, isStart: seg.isStart, isEnd: seg.isEnd, isDragging: isDragging, isSelected: instanceId === eventSelection, defaultDisplayEventEnd: defaultDisplayEventEnd }, getEventRangeMeta(eventRange, todayRange)))) : (createElement(DayGridBlockEvent, Object.assign({ eventRange: eventRange, isStart: seg.isStart, isEnd: seg.isEnd, isDragging: isDragging, isResizing: isResizing, isDateSelecting: isDateSelecting, isSelected: instanceId === eventSelection, defaultDisplayEventEnd: defaultDisplayEventEnd }, getEventRangeMeta(eventRange, todayRange))))));
        }
        return nodes;
    }
    renderFillSegs(segs, fillType) {
        const { props, context } = this;
        const { isRtl } = context;
        const { todayRange, colWidth } = props;
        const colCnt = props.cells.length;
        const nodes = [];
        for (const seg of segs) {
            const key = buildEventRangeKey(seg.eventRange); // TODO: use different type of key than fg!?
            const { left, right } = computeHorizontalsFromSeg(seg, colWidth, colCnt, isRtl);
            const isVisible = !seg.standinFor;
            nodes.push(createElement("div", { key: key, className: "fc-fill-y", style: {
                    visibility: isVisible ? '' : 'hidden',
                    left,
                    right,
                } }, fillType === 'bg-event' ?
                createElement(BgEvent, Object.assign({ eventRange: seg.eventRange, isStart: seg.isStart, isEnd: seg.isEnd }, getEventRangeMeta(seg.eventRange, todayRange))) : (renderFill(fillType))));
        }
        return createElement(Fragment, {}, ...nodes); // TODO: shouldn't this be an array, so keyed?
    }
    // Sizing
    // -----------------------------------------------------------------------------------------------
    componentDidMount() {
        const { rootEl } = this; // TODO: make dynamic with useEffect
        this.disconnectHeight = watchHeight(rootEl, (contentHeight) => {
            setRef(this.props.heightRef, contentHeight);
        });
    }
    componentWillUnmount() {
        this.disconnectHeight();
        setRef(this.props.heightRef, null);
    }
    computeFgDims() {
        const { cells } = this.props;
        const headerHeightMap = this.headerHeightRefMap.current;
        const mainHeightMap = this.mainHeightRefMap.current;
        let maxMainTop;
        let minMainBottom;
        for (const cell of cells) {
            const mainTop = headerHeightMap.get(cell.key);
            const mainHeight = mainHeightMap.get(cell.key);
            if (mainTop != null) {
                if (maxMainTop === undefined || mainTop > maxMainTop) {
                    maxMainTop = mainTop;
                }
                if (mainHeight != null) {
                    const mainBottom = mainTop + mainHeight;
                    if (minMainBottom === undefined || mainBottom < minMainBottom) {
                        minMainBottom = mainBottom;
                    }
                }
            }
        }
        return [
            maxMainTop,
            minMainBottom != null && maxMainTop != null
                ? minMainBottom - maxMainTop
                : undefined,
        ];
    }
    // Internal Utils
    // -----------------------------------------------------------------------------------------------
    getMirrorSegs() {
        let { props } = this;
        if (props.eventResize && props.eventResize.segs.length) { // messy check
            return props.eventResize.segs;
        }
        return [];
    }
    getHighlightSegs() {
        let { props } = this;
        if (props.eventDrag && props.eventDrag.segs.length) { // messy check
            return props.eventDrag.segs;
        }
        if (props.eventResize && props.eventResize.segs.length) { // messy check
            return props.eventResize.segs;
        }
        return props.dateSelectionSegs;
    }
}

class DayGridRows extends DateComponent {
    constructor() {
        super(...arguments);
        // memo
        this.splitBusinessHourSegs = memoize(splitSegsByRow);
        this.splitBgEventSegs = memoize(splitSegsByRow);
        this.splitFgEventSegs = memoize(splitSegsByRow);
        this.splitDateSelectionSegs = memoize(splitSegsByRow);
        this.splitEventDrag = memoize(splitInteractionByRow);
        this.splitEventResize = memoize(splitInteractionByRow);
        // internal
        this.rowHeightRefMap = new RefMap((height, key) => {
            // HACKy way of syncing RefMap results with prop
            const { rowHeightRefMap } = this.props;
            if (rowHeightRefMap) {
                rowHeightRefMap.handleValue(height, key);
            }
        });
        this.handleRootEl = (rootEl) => {
            this.rootEl = rootEl;
            if (rootEl) {
                this.context.registerInteractiveComponent(this, {
                    el: rootEl,
                    isHitComboAllowed: this.props.isHitComboAllowed,
                });
            }
            else {
                this.context.unregisterInteractiveComponent(this);
            }
        };
    }
    render() {
        let { props, context, rowHeightRefMap } = this;
        let { options } = context;
        let rowCnt = props.cellRows.length;
        let fgEventSegsByRow = this.splitFgEventSegs(props.fgEventSegs, rowCnt);
        let bgEventSegsByRow = this.splitBgEventSegs(props.bgEventSegs, rowCnt);
        let businessHourSegsByRow = this.splitBusinessHourSegs(props.businessHourSegs, rowCnt);
        let dateSelectionSegsByRow = this.splitDateSelectionSegs(props.dateSelectionSegs, rowCnt);
        let eventDragByRow = this.splitEventDrag(props.eventDrag, rowCnt);
        let eventResizeByRow = this.splitEventResize(props.eventResize, rowCnt);
        let isHeightAuto = getIsHeightAuto(options);
        let rowHeightsRedistribute = !props.forPrint && !isHeightAuto;
        let rowBasis = computeRowBasis(props.visibleWidth, rowCnt, isHeightAuto, options);
        let isCompact = computeRowIsCompact(props.visibleWidth, options);
        return (createElement("div", { role: 'rowgroup', className: joinClassNames(
            // HACK for Safari. Can't do break-inside:avoid with flexbox items, likely b/c it's not standard:
            // https://stackoverflow.com/a/60256345
            !props.forPrint && 'fc-flex-col', props.className), style: { width: props.width }, ref: this.handleRootEl }, props.cellRows.map((cells, row) => (createElement(DayGridRow, { key: cells[0].key, role: 'row', dateProfile: props.dateProfile, todayRange: props.todayRange, cells: cells, showDayNumbers: rowCnt > 1, showWeekNumbers: rowCnt > 1 && options.weekNumbers, forPrint: props.forPrint, isCompact: isCompact, 
            // if not auto-height, distribute height of container somewhat evently to rows
            className: joinClassNames(rowHeightsRedistribute && 'fc-grow', rowCnt > 1 && 'fc-break-inside-avoid', // don't avoid breaks for single tall row
            row < rowCnt - 1 && 'fc-border-b'), 
            // content
            fgEventSegs: fgEventSegsByRow[row], bgEventSegs: bgEventSegsByRow[row].filter(isSegAllDay) /* HACK */, businessHourSegs: businessHourSegsByRow[row], dateSelectionSegs: dateSelectionSegsByRow[row], eventSelection: props.eventSelection, eventDrag: eventDragByRow[row], eventResize: eventResizeByRow[row], dayMaxEvents: props.dayMaxEvents, dayMaxEventRows: props.dayMaxEventRows, 
            // dimensions
            colWidth: props.colWidth, basis: rowBasis, 
            // refs
            heightRef: rowHeightRefMap.createRef(cells[0].key) })))));
    }
    // Hit System
    // -----------------------------------------------------------------------------------------------
    queryHit(positionLeft, positionTop, elWidth) {
        const { props, context } = this;
        const colCnt = props.cellRows[0].length;
        const { col, left, right } = computeColFromPosition(positionLeft, elWidth, props.colWidth, colCnt, context.isRtl);
        const { row, top, bottom } = computeRowFromPosition(positionTop, props.cellRows, this.rowHeightRefMap.current);
        const cell = props.cellRows[row][col];
        const cellStartDate = cell.date;
        const cellEndDate = addDays(cellStartDate, 1);
        return {
            dateProfile: props.dateProfile,
            dateSpan: Object.assign({ range: {
                    start: cellStartDate,
                    end: cellEndDate,
                }, allDay: true }, cell.dateSpanProps),
            getDayEl: () => getCellEl(getRowEl(this.rootEl, row), col),
            rect: {
                left,
                right,
                top,
                bottom,
            },
            layer: 0,
        };
    }
}
// Utils
// -------------------------------------------------------------------------------------------------
function isSegAllDay(seg) {
    return seg.eventRange.def.allDay;
}
/*
Amount of height a row should consume prior to expanding
We don't want to use min-height with flexbox because we leverage min-height:auto,
which yields value based on natural height of events
*/
function computeRowBasis(visibleWidth, // should INCLUDE any scrollbar width to avoid oscillation
rowCnt, isHeightAuto, options) {
    if (visibleWidth != null) {
        // ensure a consistent row min-height modelled after a month with 6 rows respecting aspectRatio
        // will result in same minHeight regardless of weekends, dayMinWidth, height:auto
        const rowBasis = visibleWidth / options.aspectRatio / 6;
        // don't give minHeight when single-month non-auto-height
        // TODO: better way to detect this with DateProfile?
        return (rowCnt > 6 || isHeightAuto) ? rowBasis : 0;
    }
    return 0;
}
/*
Infers cell height based on overall width
*/
function computeRowIsCompact(visibleWidth, // should INCLUDE any scrollbar width to avoid oscillation
options) {
    if (visibleWidth != null) {
        // ensure a consistent row min-height modelled after a month with 6 rows respecting aspectRatio
        // will result in same minHeight regardless of weekends, dayMinWidth, height:auto
        const rowBasis = visibleWidth / options.aspectRatio / 6;
        return rowBasis < 70;
    }
    return false;
}

class DayGridHeaderCell extends BaseComponent {
    constructor() {
        super(...arguments);
        this.handleInnerEl = (innerEl) => {
            if (this.disconectInnerHeight) {
                this.disconectInnerHeight();
                this.disconectInnerHeight = undefined;
            }
            if (innerEl) {
                this.disconectInnerHeight = watchHeight(innerEl, (height) => {
                    setRef(this.props.innerHeightRef, height);
                });
            }
            else {
                setRef(this.props.innerHeightRef, null);
            }
        };
    }
    render() {
        const { props } = this;
        const { renderConfig, dataConfig } = props;
        // HACK
        const isDisabled = dataConfig.renderProps.isDisabled;
        return (createElement(ContentContainer, { tag: 'div', attrs: Object.assign({ role: 'columnheader', 'aria-colspan': dataConfig.colSpan }, dataConfig.attrs), className: joinClassNames('fc-header-cell fc-cell fc-flex-col fc-align-center', props.borderStart && 'fc-border-s', !props.isSticky && 'fc-crop', props.colWidth == null && 'fc-liquid', dataConfig.className), style: {
                width: props.colWidth != null
                    ? props.colWidth * (dataConfig.colSpan || 1)
                    : undefined,
            }, renderProps: dataConfig.renderProps, generatorName: renderConfig.generatorName, customGenerator: renderConfig.customGenerator, defaultGenerator: renderText, classNameGenerator: 
            // don't use custom classNames if disabled
            // TODO: make DRY with DayCellContainer
            isDisabled ? undefined : renderConfig.classNameGenerator, didMount: renderConfig.didMount, willUnmount: renderConfig.willUnmount }, (InnerContainer) => (createElement(InnerContainer, { tag: 'div', attrs: dataConfig.innerAttrs, className: joinClassNames('fc-cell-inner fc-flex-col fc-padding-sm', props.isSticky && 'fc-sticky-s'), elRef: this.handleInnerEl }))));
    }
}

class DayGridHeaderRow extends BaseComponent {
    constructor() {
        super(...arguments);
        // ref
        this.innerHeightRefMap = new RefMap(() => {
            afterSize(this.handleInnerHeights);
        });
        this.handleInnerHeights = () => {
            const innerHeightMap = this.innerHeightRefMap.current;
            let max = 0;
            for (const innerHeight of innerHeightMap.values()) {
                max = Math.max(max, innerHeight);
            }
            if (this.currentInnerHeight !== max) {
                this.currentInnerHeight = max;
                setRef(this.props.innerHeightRef, max);
            }
        };
    }
    render() {
        const { props } = this;
        return (createElement("div", { role: props.role /* !!! */, "aria-rowindex": props.rowIndex != null ? 1 + props.rowIndex : undefined, className: joinClassNames('fc-flex-row fc-content-box', props.className), style: { height: props.height } }, props.dataConfigs.map((dataConfig, cellI) => (createElement(DayGridHeaderCell, { key: dataConfig.key, renderConfig: props.renderConfig, dataConfig: dataConfig, isSticky: props.isSticky, borderStart: Boolean(cellI), colWidth: props.colWidth, innerHeightRef: props.innerHeightRef })))));
    }
    componentWillUnmount() {
        setRef(this.props.innerHeightRef, null);
    }
}

/*
TODO: kill this class in favor of DayGridHeaderRows?
*/
class DayGridHeader extends BaseComponent {
    render() {
        const { props } = this;
        return (createElement("div", { role: 'rowgroup', className: joinClassNames(props.className, 'fc-flex-col', props.width == null && 'fc-liquid'), style: {
                width: props.width
            } }, props.headerTiers.map((rowConfig, tierNum) => (createElement(DayGridHeaderRow, Object.assign({}, rowConfig, { key: tierNum, role: 'row', className: tierNum ? 'fc-border-t' : '', colWidth: props.colWidth }))))));
    }
}

class DayGridLayoutNormal extends BaseComponent {
    constructor() {
        super(...arguments);
        this.handleScroller = (scroller) => {
            setRef(this.props.scrollerRef, scroller);
        };
        this.handleTotalWidth = (totalWidth) => {
            this.setState({ totalWidth });
        };
        this.handleClientWidth = (clientWidth) => {
            this.setState({ clientWidth });
        };
    }
    render() {
        const { props, state, context } = this;
        const { options } = context;
        const { totalWidth, clientWidth } = state;
        const endScrollbarWidth = (totalWidth != null && clientWidth != null)
            ? totalWidth - clientWidth
            : undefined;
        const verticalScrollbars = !props.forPrint && !getIsHeightAuto(options);
        const stickyHeaderDates = !props.forPrint && getStickyHeaderDates(options);
        return (createElement(Fragment, null,
            options.dayHeaders && (createElement("div", { className: joinClassNames(props.forPrint ? 'fc-print-header' : 'fc-flex-row', // col for print, row for screen
                stickyHeaderDates && 'fc-table-header-sticky', 'fc-border-b') },
                createElement(DayGridHeader, { headerTiers: props.headerTiers, className: 'fc-daygrid-header' }),
                Boolean(endScrollbarWidth) && (createElement("div", { className: 'fc-border-s fc-filler', style: { minWidth: endScrollbarWidth } })))),
            createElement(Scroller, { vertical: verticalScrollbars, className: joinClassNames('fc-daygrid-body', 
                // HACK for Safari. Can't do break-inside:avoid with flexbox items, likely b/c it's not standard:
                // https://stackoverflow.com/a/60256345
                !props.forPrint && 'fc-flex-col', verticalScrollbars && 'fc-liquid'), ref: this.handleScroller, clientWidthRef: this.handleClientWidth },
                createElement(DayGridRows, { dateProfile: props.dateProfile, todayRange: props.todayRange, cellRows: props.cellRows, forPrint: props.forPrint, isHitComboAllowed: props.isHitComboAllowed, className: 'fc-grow', dayMaxEvents: props.forPrint ? undefined : options.dayMaxEvents, dayMaxEventRows: options.dayMaxEventRows, 
                    // content
                    fgEventSegs: props.fgEventSegs, bgEventSegs: props.bgEventSegs, businessHourSegs: props.businessHourSegs, dateSelectionSegs: props.dateSelectionSegs, eventDrag: props.eventDrag, eventResize: props.eventResize, eventSelection: props.eventSelection, 
                    // dimensions
                    visibleWidth: totalWidth, 
                    // refs
                    rowHeightRefMap: props.rowHeightRefMap })),
            createElement(Ruler, { widthRef: this.handleTotalWidth })));
    }
}

class DayGridLayoutPannable extends BaseComponent {
    constructor() {
        super(...arguments);
        this.headerScrollerRef = createRef();
        this.bodyScrollerRef = createRef();
        this.footerScrollerRef = createRef();
        // Sizing
        // -----------------------------------------------------------------------------------------------
        this.handleTotalWidth = (totalWidth) => {
            this.setState({ totalWidth });
        };
        this.handleClientWidth = (clientWidth) => {
            this.setState({ clientWidth });
        };
    }
    render() {
        const { props, state, context } = this;
        const { options } = context;
        const { totalWidth, clientWidth } = state;
        const endScrollbarWidth = (totalWidth != null && clientWidth != null)
            ? totalWidth - clientWidth
            : undefined;
        const verticalScrollbars = !props.forPrint && !getIsHeightAuto(options);
        const stickyHeaderDates = !props.forPrint && getStickyHeaderDates(options);
        const stickyFooterScrollbar = !props.forPrint && getStickyFooterScrollbar(options);
        const colCnt = props.cellRows[0].length;
        const [canvasWidth, colWidth] = computeColWidth(colCnt, props.dayMinWidth, clientWidth);
        return (createElement(Fragment, null,
            options.dayHeaders && (createElement("div", { className: joinClassNames('fc-print-header', stickyHeaderDates && 'fc-table-header-sticky') },
                createElement(Scroller, { horizontal: true, hideScrollbars: true, className: 'fc-daygrid-header fc-flex-row fc-border-b', ref: this.headerScrollerRef },
                    createElement(DayGridHeader, { headerTiers: props.headerTiers, colWidth: colWidth, width: canvasWidth }),
                    Boolean(endScrollbarWidth) && (createElement("div", { className: 'fc-border-s fc-filler', style: { minWidth: endScrollbarWidth } }))))),
            createElement(Scroller, { vertical: verticalScrollbars, horizontal: true, hideScrollbars: stickyFooterScrollbar ||
                    props.forPrint // prevents blank space in print-view on Safari
                , className: joinClassNames('fc-daygrid-body', 
                // HACK for Safari. Can't do break-inside:avoid with flexbox items, likely b/c it's not standard:
                // https://stackoverflow.com/a/60256345
                !props.forPrint && 'fc-flex-col', verticalScrollbars && 'fc-liquid'), ref: this.bodyScrollerRef, clientWidthRef: this.handleClientWidth },
                createElement(DayGridRows, { dateProfile: props.dateProfile, todayRange: props.todayRange, cellRows: props.cellRows, forPrint: props.forPrint, isHitComboAllowed: props.isHitComboAllowed, className: 'fc-grow', dayMaxEvents: props.forPrint ? undefined : options.dayMaxEvents, dayMaxEventRows: options.dayMaxEventRows, 
                    // content
                    fgEventSegs: props.fgEventSegs, bgEventSegs: props.bgEventSegs, businessHourSegs: props.businessHourSegs, dateSelectionSegs: props.dateSelectionSegs, eventDrag: props.eventDrag, eventResize: props.eventResize, eventSelection: props.eventSelection, 
                    // dimensions
                    colWidth: colWidth, width: canvasWidth, visibleWidth: totalWidth, 
                    // refs
                    rowHeightRefMap: props.rowHeightRefMap })),
            Boolean(stickyFooterScrollbar) && (createElement(FooterScrollbar, { isSticky: true, canvasWidth: canvasWidth, scrollerRef: this.footerScrollerRef })),
            createElement(Ruler, { widthRef: this.handleTotalWidth })));
    }
    // Lifecycle
    // -----------------------------------------------------------------------------------------------
    componentDidMount() {
        // scroller
        const ScrollerSyncer = getScrollerSyncerClass(this.context.pluginHooks);
        this.syncedScroller = new ScrollerSyncer(true); // horizontal=true
        setRef(this.props.scrollerRef, this.syncedScroller);
        this.updateSyncedScroller();
    }
    componentDidUpdate() {
        // scroller
        this.updateSyncedScroller();
    }
    componentWillUnmount() {
        // scroller
        this.syncedScroller.destroy();
    }
    // Scrolling
    // -----------------------------------------------------------------------------------------------
    updateSyncedScroller() {
        this.syncedScroller.handleChildren([
            this.headerScrollerRef.current,
            this.bodyScrollerRef.current,
            this.footerScrollerRef.current,
        ]);
    }
}

class DayGridLayout extends BaseComponent {
    constructor() {
        super(...arguments);
        // ref
        this.scrollerRef = createRef();
        this.rowHeightRefMap = new RefMap(() => {
            afterSize(this.updateScrollY);
        });
        // internal
        this.scrollDate = null;
        this.updateScrollY = () => {
            const rowHeightMap = this.rowHeightRefMap.current;
            const scroller = this.scrollerRef.current;
            // Since updateScrollY is called by rowHeightRefMap, could be called with null during cleanup,
            // and the scroller might not exist
            if (scroller && this.scrollDate) {
                let scrollTop = computeTopFromDate(this.scrollDate, this.props.cellRows, rowHeightMap, 1);
                if (scrollTop != null) {
                    if (scrollTop) {
                        scrollTop++; // clear top border
                    }
                    scroller.scrollTo({ y: scrollTop });
                }
            }
        };
        this.handleScrollEnd = (isUser) => {
            if (isUser) {
                this.scrollDate = null;
            }
        };
    }
    render() {
        const { props, context } = this;
        const { options } = context;
        const commonLayoutProps = Object.assign(Object.assign({}, props), { scrollerRef: this.scrollerRef, rowHeightRefMap: this.rowHeightRefMap });
        return (createElement(ViewContainer, { viewSpec: context.viewSpec, attrs: {
                role: 'grid',
                'aria-rowcount': props.headerTiers.length + props.cellRows.length,
                'aria-colcount': props.cellRows[0].length,
                'aria-labelledby': props.labelId,
                'aria-label': props.labelStr,
            }, className: joinClassNames(props.className, 'fc-print-root fc-border') }, options.dayMinWidth ? (createElement(DayGridLayoutPannable, Object.assign({}, commonLayoutProps, { dayMinWidth: options.dayMinWidth }))) : (createElement(DayGridLayoutNormal, Object.assign({}, commonLayoutProps)))));
    }
    // Lifecycle
    // -----------------------------------------------------------------------------------------------
    componentDidMount() {
        this.resetScroll();
        this.scrollerRef.current.addScrollEndListener(this.handleScrollEnd);
    }
    componentDidUpdate(prevProps) {
        if (prevProps.dateProfile !== this.props.dateProfile && this.context.options.scrollTimeReset) {
            this.resetScroll();
        }
    }
    componentWillUnmount() {
        this.scrollerRef.current.removeScrollEndListener(this.handleScrollEnd);
    }
    // Scrolling
    // -----------------------------------------------------------------------------------------------
    resetScroll() {
        this.scrollDate = this.props.dateProfile.currentDate;
        this.updateScrollY();
        const scroller = this.scrollerRef.current;
        scroller.scrollTo({ x: 0 });
    }
}

class DayGridView extends BaseComponent {
    constructor() {
        super(...arguments);
        // memo
        this.buildDayTableModel = memoize(buildDayTableModel);
        this.buildDateRowConfigs = memoize(buildDateRowConfigs);
        this.createDayHeaderFormatter = memoize(createDayHeaderFormatter);
        // internal
        this.slicer = new DayTableSlicer();
    }
    render() {
        const { props, context } = this;
        const { options } = context;
        const dayTableModel = this.buildDayTableModel(props.dateProfile, context.dateProfileGenerator);
        const datesRepDistinctDays = dayTableModel.rowCnt === 1;
        const dayHeaderFormat = this.createDayHeaderFormatter(context.options.dayHeaderFormat, datesRepDistinctDays, dayTableModel.colCnt);
        const slicedProps = this.slicer.sliceProps(props, props.dateProfile, options.nextDayThreshold, context, dayTableModel);
        return (createElement(NowTimer, { unit: "day" }, (nowDate, todayRange) => {
            const headerTiers = this.buildDateRowConfigs(dayTableModel.headerDates, datesRepDistinctDays, props.dateProfile, todayRange, dayHeaderFormat, context);
            return (createElement(DayGridLayout, { labelId: props.labelId, labelStr: props.labelStr, dateProfile: props.dateProfile, todayRange: todayRange, cellRows: dayTableModel.cellRows, forPrint: props.forPrint, className: 'fc-daygrid', 
                // header content
                headerTiers: headerTiers, 
                // body content
                fgEventSegs: slicedProps.fgEventSegs, bgEventSegs: slicedProps.bgEventSegs, businessHourSegs: slicedProps.businessHourSegs, dateSelectionSegs: slicedProps.dateSelectionSegs, eventDrag: slicedProps.eventDrag, eventResize: slicedProps.eventResize, eventSelection: slicedProps.eventSelection }));
        }));
    }
}

class TableDateProfileGenerator extends DateProfileGenerator {
    // Computes the date range that will be rendered
    buildRenderRange(currentRange, currentRangeUnit, isRangeAllDay) {
        let renderRange = super.buildRenderRange(currentRange, currentRangeUnit, isRangeAllDay);
        let { props } = this;
        return buildDayTableRenderRange({
            currentRange: renderRange,
            snapToWeek: /^(year|month)$/.test(currentRangeUnit),
            fixedWeekCount: props.fixedWeekCount,
            dateEnv: props.dateEnv,
        });
    }
}
function buildDayTableRenderRange(props) {
    let { dateEnv, currentRange } = props;
    let { start, end } = currentRange;
    let endOfWeek;
    // year and month views should be aligned with weeks. this is already done for week
    if (props.snapToWeek) {
        start = dateEnv.startOfWeek(start);
        // make end-of-week if not already
        endOfWeek = dateEnv.startOfWeek(end);
        if (endOfWeek.valueOf() !== end.valueOf()) {
            end = addWeeks(endOfWeek, 1);
        }
    }
    // ensure 6 weeks
    if (props.fixedWeekCount) {
        // TODO: instead of these date-math gymnastics (for multimonth view),
        // compute dateprofiles of all months, then use start of first and end of last.
        let lastMonthRenderStart = dateEnv.startOfWeek(dateEnv.startOfMonth(addDays(currentRange.end, -1)));
        let rowCnt = Math.ceil(// could be partial weeks due to hiddenDays
        diffWeeks(lastMonthRenderStart, end));
        end = addWeeks(end, 6 - rowCnt);
    }
    return { start, end };
}

var css_248z = ":root{--fc-daygrid-event-dot-width:8px}.fc-daygrid-row-print{min-height:6em}.fc-daygrid-day.fc-day-today{background-color:var(--fc-today-bg-color)}.fc-daygrid-day-header{display:flex;flex-direction:row-reverse}.fc-day-other .fc-daygrid-day-header{opacity:.3}.fc-daygrid-day-number{padding:4px;position:relative}.fc-daygrid-month-start{font-size:1.1em;font-weight:700}.fc-daygrid-day-body{display:flex;flex-direction:column;margin-bottom:1px}.fc-daygrid-day-body-tall{margin-bottom:1em;min-height:2em}.fc-daygrid-day-body:only-child{margin-top:2px}.fc-daygrid-more-link{border-radius:3px;cursor:pointer;font-size:var(--fc-small-font-size);margin:0 2px 1px;max-width:100%;overflow:hidden;padding:2px;position:relative;white-space:nowrap}.fc-daygrid-more-link:hover{background-color:rgba(0,0,0,.1)}.fc-daygrid-more-link-button{align-self:flex-start}.fc-daygrid-more-link-block{border:1px solid var(--fc-event-border-color);padding:1px}.fc-daygrid-week-number{background-color:var(--fc-neutral-bg-color);color:var(--fc-neutral-text-color);min-width:1.5em;padding:2px;position:absolute;text-align:center;top:0;z-index:1}.fc-direction-ltr .fc-daygrid-week-number{border-radius:0 0 3px}.fc-direction-rtl .fc-daygrid-week-number{border-radius:0 0 0 3px}.fc-more-popover .fc-popover-body{min-width:220px;padding:10px}.fc-daygrid-event{border-radius:3px;font-size:var(--fc-small-font-size);margin-bottom:1px}.fc-direction-ltr .fc-daygrid-event.fc-event-start,.fc-direction-rtl .fc-daygrid-event.fc-event-end{margin-left:2px}.fc-direction-ltr .fc-daygrid-event.fc-event-end,.fc-direction-rtl .fc-daygrid-event.fc-event-start{margin-right:2px}.fc-direction-ltr .fc-daygrid-event .fc-event-time{margin-right:3px}.fc-direction-rtl .fc-daygrid-event .fc-event-time{margin-left:3px}.fc-direction-ltr .fc-daygrid-block-event:not(.fc-event-start),.fc-direction-rtl .fc-daygrid-block-event:not(.fc-event-end){border-bottom-left-radius:0;border-left-width:0;border-top-left-radius:0}.fc-direction-ltr .fc-daygrid-block-event:not(.fc-event-end),.fc-direction-rtl .fc-daygrid-block-event:not(.fc-event-start){border-bottom-right-radius:0;border-right-width:0;border-top-right-radius:0}.fc-daygrid-block-event .fc-event-time{font-weight:700}.fc-daygrid-block-event .fc-event-time,.fc-daygrid-block-event .fc-event-title{padding:1px}.fc-daygrid-dot-event{align-items:center;direction:row;display:flex;padding:2px 0;position:relative}.fc-daygrid-dot-event.fc-event-mirror,.fc-daygrid-dot-event:hover{background:rgba(0,0,0,.1)}.fc-daygrid-dot-event.fc-event-selected:before{bottom:-10px;top:-10px}.fc-daygrid-event-dot{border:calc(var(--fc-daygrid-event-dot-width)/2) solid var(--fc-event-border-color);border-radius:calc(var(--fc-daygrid-event-dot-width)/2);box-sizing:content-box;height:0;margin:0 4px;width:0}.fc-daygrid-dot-event .fc-event-time,.fc-daygrid-dot-event .fc-event-title{overflow:hidden;white-space:nowrap}.fc-media-print .fc-daygrid-dot-event .fc-event-time,.fc-media-print .fc-daygrid-dot-event .fc-event-title{overflow:hidden!important;white-space:nowrap!important}.fc-daygrid-dot-event .fc-event-title{flex-basis:0;flex-grow:1;font-weight:700;min-height:0;min-width:0}";
injectStyles(css_248z);

export { DayGridHeaderRow, DayGridLayout, DayGridRow, DayGridRows, DayGridView, DayTableSlicer, TableDateProfileGenerator, buildDateDataConfigs, buildDateRenderConfig, buildDateRowConfig, buildDateRowConfigs, buildDayTableModel, buildDayTableRenderRange, computeColFromPosition, computeColWidth, computeRowBasis, computeRowIsCompact, createDayHeaderFormatter, getCellEl, getRowEl };
